
import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';

interface PricingPackage {
  name: string;
  visitorPrice: string;
  memberPrice: string;
  description: string;
}

interface TreatmentPageProps {
  title: string;
  subtitle: string;
  description: string;
  whoItsFor: string[];
  packages: PricingPackage[];
  beforeCare: string[];
  duringCare: string[];
  afterCare: string[];
  memberPerks: string[];
  faqs?: { question: string; answer: string }[];
}

const TreatmentPageTemplate = ({
  title,
  subtitle,
  description,
  whoItsFor,
  packages,
  beforeCare,
  duringCare,
  afterCare,
  memberPerks
}: TreatmentPageProps) => {
  const [showMemberPricing, setShowMemberPricing] = useState(false);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="apple-section bg-gradient-to-br from-juv-soft-blush to-white">
        <div className="apple-container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="apple-headline text-juv-deep-chestnut mb-6 font-serif">
              {title}
            </h1>
            <p className="apple-subheadline text-juv-deep-chestnut/70 mb-8">
              {subtitle}
            </p>
            <p className="apple-body text-juv-deep-chestnut/70 max-w-3xl mx-auto">
              {description}
            </p>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-12 bg-white">
        <div className="apple-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-8 text-center">
              Perfect For
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {whoItsFor.map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-juv-terracotta rounded-full"></div>
                  <span className="apple-body text-juv-deep-chestnut/70">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="apple-section bg-gradient-to-br from-juv-soft-blush/20 to-white">
        <div className="apple-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">
                Transparent Pricing
              </h2>
              <div className="flex items-center justify-center space-x-4">
                <span className={`font-medium ${!showMemberPricing ? 'text-juv-deep-chestnut' : 'text-juv-deep-chestnut/70'}`}>
                  Visitor Pricing
                </span>
                <Switch
                  checked={showMemberPricing}
                  onCheckedChange={setShowMemberPricing}
                />
                <span className={`font-medium ${showMemberPricing ? 'text-juv-terracotta' : 'text-juv-deep-chestnut/70'}`}>
                  Member Pricing ✨
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg, index) => (
                <Card key={index} className="apple-card border-juv-rose-clay/20">
                  <CardContent className="p-6">
                    <CardTitle className="text-xl font-semibold text-juv-deep-chestnut mb-2">
                      {pkg.name}
                    </CardTitle>
                    <div className="text-3xl font-bold mb-2">
                      <span className={showMemberPricing ? 'text-juv-terracotta' : 'text-juv-deep-chestnut'}>
                        {showMemberPricing ? pkg.memberPrice : pkg.visitorPrice}
                      </span>
                    </div>
                    <CardDescription className="apple-body text-juv-deep-chestnut/70 mb-4">
                      {pkg.description}
                    </CardDescription>
                    <Button className="w-full bg-juv-gradient-primary text-white apple-hover-scale">
                      Book Now
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="apple-section bg-white">
        <div className="apple-container">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-12 text-center">
              What to Expect
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: "Before", items: beforeCare, number: "1" },
                { title: "During", items: duringCare, number: "2" },
                { title: "After", items: afterCare, number: "3" }
              ].map((section, index) => (
                <Card key={index} className="apple-card border-juv-rose-clay/20">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-juv-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold">{section.number}</span>
                    </div>
                    <CardTitle className="text-lg font-semibold text-juv-deep-chestnut mb-4">
                      {section.title}
                    </CardTitle>
                    <ul className="apple-spacing-xs text-sm text-juv-deep-chestnut/70">
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="apple-caption">{item}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Member Perks */}
      <section className="apple-section bg-gradient-to-br from-juv-soft-blush/30 to-white">
        <div className="apple-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-8 text-center">
              Glow Member Benefits
            </h2>
            <div className="glass-panel p-8">
              <div className="apple-spacing-sm">
                {memberPerks.map((perk, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-juv-gradient-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                    <span className="apple-body text-juv-deep-chestnut/70">{perk}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <Button asChild size="lg" className="bg-juv-gradient-primary text-white apple-hover-scale">
                  <Link to="/memberships">Join the Glow Plan</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-t border-juv-rose-clay/20 p-4 safe-area-bottom">
        <div className="apple-container">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <div className="hidden md:block">
              <p className="font-semibold text-juv-deep-chestnut">{title}</p>
              <p className="apple-caption text-juv-deep-chestnut/70">Ready to book or have questions?</p>
            </div>
            <div className="flex space-x-3 w-full md:w-auto">
              <Button className="flex-1 md:flex-none bg-juv-deep-chestnut hover:bg-juv-terracotta text-white apple-hover-scale">
                Book Now
              </Button>
              <Button variant="outline" className="flex-1 md:flex-none border-juv-rose-clay text-juv-terracotta hover:bg-juv-soft-blush hover:text-juv-deep-chestnut apple-hover-scale">
                Message Us
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TreatmentPageTemplate;
